import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Modal, Space, Table, message, Pagination } from "antd"; // Import Pagination
import axios from "axios";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";
import AddMarque from "./AddMarque";
import ActionDropdown from "../../production/components/ActionDropdown";

const Marques = () => {
  const [marques, setMarques] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMarque, setSelectedMarque] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false); // New state for table loading

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Allow changing page size, default 10

  const handleShow = (edit = false, marque = null) => {
    setEditMode(edit);
    setSelectedMarque(marque);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedMarque(null);
  };

  const fetchMarques = async () => {
    setLoadingTable(true); // Start loading
    try {
      const res = await axios.get(`${API_URL}/api/parametre/type/MARQUE`, {
        headers: {
          Authorization: `Bearer ${TOKEN}`,
        },
      });
      // Sort by libelleParametre alphabetically (French locale, case/accent insensitive)
      const sortedMarques = [...res.data].sort((a, b) =>
        (a.libelleParametre || "").localeCompare(
          b.libelleParametre || "",
          "fr",
          { sensitivity: "base" }
        )
      );
      setMarques(sortedMarques);
    } catch (err) {
      console.error(err); // Use console.error
      message.error("Erreur lors de la récupération des marques.");
    } finally {
      setLoadingTable(false); // End loading
    }
  };

  useEffect(() => {
    fetchMarques();
  }, []); // Fetch on initial component mount only

  // --- Handlers for Add and Update to keep new/modified at top ---
  const handleAddSuccess = (newMarque) => {
    setMarques((prev) =>
      [...prev, newMarque].sort((a, b) =>
        (a.libelleParametre || "").localeCompare(
          b.libelleParametre || "",
          "fr",
          { sensitivity: "base" }
        )
      )
    );
    setCurrentPage(1);
  };

  const handleUpdateSuccess = (updatedMarque) => {
    setMarques((prev) => {
      const withoutOld = prev.filter(
        (marque) => marque.parametreId !== updatedMarque.parametreId
      );
      return [...withoutOld, updatedMarque].sort((a, b) =>
        (a.libelleParametre || "").localeCompare(
          b.libelleParametre || "",
          "fr",
          { sensitivity: "base" }
        )
      );
    });
    // No need to change page, as the updated item would already be on a visible page,
    // or it will jump to the current page if it's placed at the top.
  };
  // --- End Handlers ---

  const showDeleteConfirm = (parametreId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette marque ?",
      icon: <ExclamationCircleOutlined />,
      content: "Cette action est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => { // Made onOk async
        try {
          await axios.delete(`${API_URL}/api/parametre/${parametreId}`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
            },
          });
          setMarques((prev) =>
            prev.filter((marque) => marque.parametreId !== parametreId)
          );
          toast.success("Marque supprimée avec succès.");

          // After deletion, check if the current page is now empty
          // or if the last item on the last page was deleted.
          const totalItemsAfterDeletion = marques.length - 1;
          const totalPagesAfterDeletion = Math.ceil(totalItemsAfterDeletion / pageSize);
          if (currentPage > totalPagesAfterDeletion && totalPagesAfterDeletion > 0) {
            setCurrentPage(totalPagesAfterDeletion);
          } else if (totalPagesAfterDeletion === 0) {
            setCurrentPage(1); // Reset to page 1 if all items are deleted
          }
        } catch (err) {
          console.error(err); // Use console.error
          toast.error("Erreur lors de la suppression de la marque.");
        }
      },
    });
  };

  // Ant Design Table uses its own pagination, so we only need to slice if not using AntD's internal pagination.
  // We'll keep the `paginatedData` for manual control if needed, but AntD's Table component
  // will handle it if `pagination` prop is configured.
  // If you decide to use Ant Design's Table's internal pagination, you can remove `paginatedData` and use `marques` directly.
  // For now, let's keep it consistent with the other components using manual slice for pagination display.
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = marques.slice(startIndex, endIndex);


  const columns = [
    {
      title: "Nom",
      dataIndex: "libelleParametre",
      key: "libelleParametre",
    },
    {
      title: "Catégorie",
      dataIndex: ["categorie", "nom"],
      key: "categorie",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
         <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <EditOutlined />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => showDeleteConfirm(record.parametreId),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow()}
              >
                Ajouter une nouvelle marque
              </Button>
              <div className="card">
                <h5 className="card-header">Paramétrage de marques</h5>
                {loadingTable ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Table
                      columns={columns}
                      dataSource={paginatedData} // Use paginatedData here
                      rowKey="parametreId"
                      pagination={false} // Disable Ant Design's internal pagination
                      // Ant Design Table also has a `loading` prop if you want to use its built-in spinner:
                      // loading={loadingTable}
                    />
                    {marques.length > 0 ? ( // Only show pagination if there are items
                      <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                        <Pagination
                          current={currentPage}
                          pageSize={pageSize}
                          total={marques.length}
                          onChange={(page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                          }}
                          showSizeChanger
                          pageSizeOptions={['5', '10', '20', '50']}
                          showTotal={(total, range) => `${range[0]}-${range[1]} sur ${total} éléments`}
                        />
                      </div>
                    ) : (
                      !loadingTable && <p className="text-center p-3">Aucune marque trouvée.</p>
                    )}
                  </>
                )}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddMarque
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedMarque={selectedMarque}
        onAddSuccess={handleAddSuccess}
        onUpdateSuccess={handleUpdateSuccess} // Pass the new handler
        existingMarques={marques} // Pass the full list for client-side duplicate check
      />
    </div>
  );
};

export default Marques;
